import React from 'react';
import { Helmet } from 'react-helmet-async';
import { EventDesignPurpose } from '@graphql/generated-base';
import { deriveWebsiteColorsFromEventDesign } from '@shared/utils/websiteDesign';
import { useRegistrationFormMatching } from '@apps/guest/components/RegistrationFormTypographyProvider/useRegistrationFormMatching';
import { useRegistrationEventDesignQuery } from './Registration.graphql.generated';

// pageColor is interpolated into a <style> tag; backend-controlled today, but allowlist safe color formats
// (hex / rgb[a] / hsl[a]) as defense-in-depth so malformed upstream data can't break out of the rule.
const SAFE_CSS_COLOR = /^#[0-9a-fA-F]{3,8}$|^rgba?\([\d.,%\s/]+\)$|^hsla?\([\d.,%\s/]+\)$/;

// SSR-injects the event's page background onto html/body so the registration deep-link's first paint is
// already themed (no white flash) — the form body is NoSsr, so nothing else paints a background server-
// side. pageColor is derived exactly as the form's ColorPaletteProvider does, so it matches the mounted
// form's background (no reskin). Only emitted when "Make Registration look like my site" is enabled.
export const RegistrationThemeBackground: React.FC<{ eventHandle: string }> = ({ eventHandle }) => {
  // Fetch unconditionally (ssr:true) so it resolves in parallel with the other SSR queries rather than
  // chaining behind the matching consent; gate only the emission below. Also SSR-caches the design, so
  // the form's own cache-first query reuses it instead of refetching on the client.
  const { data } = useRegistrationEventDesignQuery({
    variables: { eventHandle, eventDesignPurpose: EventDesignPurpose.live },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: true,
  });
  const { isEnabled } = useRegistrationFormMatching();

  const eventDesign = data?.eventByName?.eventDesign;
  if (!isEnabled || !eventDesign) {
    return null;
  }

  const { pageColor } = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);
  if (!pageColor || !SAFE_CSS_COLOR.test(pageColor)) {
    return null;
  }

  return (
    <Helmet>
      <style>{`html,body{background-color:${pageColor};}`}</style>
    </Helmet>
  );
};
